<template>
  <v-container fluid class="patient-bar">
    <h3 class="multi-patient-bar__label">Identify Patient</h3>

    <!-- Search form: always visible -->
    <v-form @submit.prevent>
      <v-row dense>
        <v-col v-if="allSelectedPatients.length === 0" cols="12">
          <v-alert type="warning" variant="tonal" density="compact">
            <v-icon>mdi-account-search</v-icon> No patient selected. Use the fields below to search for an existing
            patient, then click <strong>Search Patient</strong>. Or click <strong>Register New Patient</strong> to
            create one.
          </v-alert>
        </v-col>
        <v-col :cols="3">
          <GuessMetadataHeader
            v-if="rowGuessMeta(['firstName', 'lastName', 'phoneNumber', 'dob'])"
            :metadata="guessMetaData('firstName')"
            label="First Name"
            @highlight-bounding-box="onHighlightBoundingBox" />
          <v-text-field v-model="searchCriteria.firstName" :disabled="!props.editable" type="text" label="First Name" />
        </v-col>
        <v-col :cols="3">
          <GuessMetadataHeader
            v-if="rowGuessMeta(['firstName', 'lastName', 'phoneNumber', 'dob'])"
            :metadata="guessMetaData('lastName')"
            label="Last Name"
            @highlight-bounding-box="onHighlightBoundingBox" />
          <v-text-field v-model="searchCriteria.lastName" :disabled="!props.editable" type="text" label="Last Name" />
        </v-col>
        <v-col :cols="3">
          <GuessMetadataHeader
            v-if="rowGuessMeta(['firstName', 'lastName', 'phoneNumber', 'dob'])"
            :metadata="guessMetaData('phoneNumber')"
            label="Phone Number"
            @highlight-bounding-box="onHighlightBoundingBox" />
          <v-text-field
            v-model="searchCriteria.phoneNumber"
            :disabled="!props.editable"
            type="text"
            label="Phone Number" />
        </v-col>
        <v-col :cols="3">
          <GuessMetadataHeader
            v-if="rowGuessMeta(['firstName', 'lastName', 'phoneNumber', 'dob'])"
            :metadata="guessMetaData('dob')"
            label="Date of Birth"
            @highlight-bounding-box="onHighlightBoundingBox" />
          <text-date-input v-model="searchCriteria.dob" :disabled="!props.editable" label="Date of Birth" />
        </v-col>
        <v-col cols="4">
          <GuessMetadataHeader
            v-if="rowGuessMeta(['address'])"
            :metadata="guessMetaData('address')"
            label="Address"
            @highlight-bounding-box="onHighlightBoundingBox" />
          <v-text-field v-model="searchCriteria.address" :disabled="!props.editable" type="text" label="Address" />
        </v-col>
        <v-col cols="3">
          <GuessMetadataHeader v-if="rowGuessMeta(['address'])" />
          <v-text-field
            v-model="searchCriteria.externalId"
            :disabled="!props.editable"
            type="text"
            label="External ID" />
        </v-col>
      </v-row>
      <v-row v-if="props.editable">
        <v-col cols="auto">
          <v-btn color="primary" variant="flat" rounded prepend-icon="mdi-magnify" @click="searchPatient">
            Search Patient
          </v-btn>
        </v-col>
        <v-col cols="auto">
          <v-btn
            v-if="props.allowNewPatientRegistration"
            color="primary"
            variant="tonal"
            rounded
            prepend-icon="mdi-account-plus-outline"
            @click="openCreateModal">
            Register New Patient
          </v-btn>
        </v-col>
      </v-row>
    </v-form>

    <!-- Search results dialog: multi-select with checkboxes -->
    <v-dialog v-model="searchDialogOpen" max-width="900">
      <v-card>
        <v-card-title class="text-h6">Select Patients</v-card-title>
        <v-card-text>
          <v-progress-linear v-if="searchLoading" indeterminate class="mb-4" />
          <template v-else>
            <div v-if="!searchResults || !searchResults.length" class="text-medium-emphasis">No patients found</div>
            <template v-else>
              <v-alert type="info" variant="tonal" density="compact" class="mb-3">
                The first patient selected will be the <strong>Primary</strong> chart used for email templates and
                messaging.
              </v-alert>
              <v-list lines="two" density="comfortable">
                <v-list-item
                  v-for="p in searchResults"
                  :key="p.id"
                  :class="{ 'bg-primary-lighten-5': pendingIds.includes(p.id) }"
                  @click="toggleSelection(p)">
                  <template #prepend>
                    <v-checkbox
                      :model-value="pendingIds.includes(p.id)"
                      @update:model-value="toggleSelection(p)"
                      @click.stop />
                  </template>
                  <v-list-item-title>
                    {{ p.firstName }} {{ p.lastName }}
                    <v-chip v-if="pendingIds[0] === p.id" size="x-small" color="primary" variant="tonal" class="ml-2">
                      Primary
                    </v-chip>
                  </v-list-item-title>
                  <v-list-item-subtitle>
                    <span v-if="p.dob">{{ convertDateToMMDDYYYY(p.dob) }}</span>
                    <span v-else> DOB: N/A </span>
                    <span v-if="p.phoneNumber"> ••• {{ p.phoneNumber }}</span>
                    <span v-else> ••• PHONE: N/A </span>
                    <span v-if="p.address"> ••• {{ p.address }}</span>
                    <span v-if="p.externalId"> ••• External ID: {{ p.externalId }}</span>
                    <span v-if="showTeamField && p.extraData?.team"> ••• Team: {{ p.extraData.team }}</span>
                  </v-list-item-subtitle>
                </v-list-item>
              </v-list>
            </template>
          </template>
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn-lh variant="text" :disabled="props.confirmSelectionLoading" @click="searchDialogOpen = false">
            Close
          </v-btn-lh>
          <v-btn-lh
            v-if="pendingIds.length > 0"
            color="primary"
            :disabled="props.confirmSelectionLoading"
            :loading="props.confirmSelectionLoading"
            @click="confirmSelection">
            Confirm Selection ({{ pendingIds.length }})
          </v-btn-lh>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Selected patient cards: always visible when patients are selected -->
    <div v-if="allSelectedPatients.length > 0" class="mt-4">
      <h4 class="multi-patient-bar__label mb-2">Selected Patient Charts</h4>
      <div class="d-flex flex-wrap ga-3">
        <PatientCard
          v-for="p in allSelectedPatients"
          :key="p.id"
          :patient="p"
          :is-primary="p.id === patient?.id"
          :editable="props.editable"
          :show-status="showStatusField"
          :show-external-id="showExternalIdField"
          :show-chart-type="showChartTypeField"
          :show-team="showTeamField"
          @edit="openEditModal"
          @remove="onRemovePatient" />
      </div>
    </div>

    <!-- Edit patient modal -->
    <PatientEditModal
      v-model="editModalOpen"
      :patient="editingPatient"
      :client-company="props.clientCompany"
      mode="edit"
      @save="onSavePatientEdit" />

    <!-- Create patient modal -->
    <PatientEditModal
      v-model="createModalOpen"
      :patient="newPatientDefaults"
      :client-company="props.clientCompany"
      mode="create"
      @create="onCreatePatient" />
  </v-container>
</template>

<script setup>
import { Patient } from "@/models/intake";
import { ref, computed, onMounted } from "vue";
import { patientService } from "@/services/patientService";
import { useDate } from "vuetify";
import { useRequirementsStore } from "@/stores/requirementsStore";
import { convertDateToMMDDYYYY } from "@/utils";
import PatientCard from "./PatientCard.vue";
import PatientEditModal from "./PatientEditModal.vue";
import GuessMetadataHeader from "./GuessMetadataHeader.vue";

const dateAdapter = useDate();
const requirementsStore = useRequirementsStore();

const showStatusField = computed(
  () => requirementsStore.clientConfig?.intake?.extraFields?.includes("patient.status") || false
);
const showExternalIdField = computed(
  () => requirementsStore.clientConfig?.intake?.extraFields?.includes("patient.externalId") || false
);
const showChartTypeField = computed(
  () => requirementsStore.clientConfig?.intake?.extraFields?.includes("patient.chartType") || false
);
const showTeamField = computed(
  () => requirementsStore.clientConfig?.intake?.extraFields?.includes("patient.team") || false
);

const emit = defineEmits([
  "set-patient",
  "save-new-patient",
  "set-additional-patients",
  "confirm-selection",
  "update-patient",
  "highlight-bounding-box",
]);
const props = defineProps({
  patient: {
    required: false,
    type: Patient,
    default: null,
  },
  additionalPatients: {
    required: false,
    type: Array,
    default: () => [],
  },
  patientGuess: {
    required: false,
    type: Object,
    default: null,
  },
  clientCompany: {
    required: true,
    type: Number,
  },
  editable: {
    required: false,
    type: Boolean,
    default: false,
  },
  allowNewPatientRegistration: {
    required: false,
    type: Boolean,
    default: true,
  },
  confirmSelectionLoading: {
    required: false,
    type: Boolean,
    default: false,
  },
});

// Search state
const searchCriteria = ref(new Patient({}, dateAdapter));
const searchDialogOpen = ref(false);
const searchResults = ref([]);
const searchLoading = ref(false);

// Multi-select pending state (before confirm)
const pendingIds = ref([]);
const pendingPatients = ref([]);

// Edit modal state
const editModalOpen = ref(false);
const editingPatient = ref(null);

// Create modal state
const createModalOpen = ref(false);
const newPatientDefaults = ref(null);

const patient = computed(() => props.patient);

const guessMetaData = (field) => props.patientGuess?.[`${field}MetaData`];

const rowGuessMeta = (fields) => fields.some((field) => guessMetaData(field));

const onHighlightBoundingBox = (metadata) => emit("highlight-bounding-box", metadata);

// Combined list: primary patient + additional patients (deduped)
const allSelectedPatients = computed(() => {
  const patients = [];
  if (patient.value) {
    patients.push(patient.value);
  }
  if (props.additionalPatients?.length > 0) {
    props.additionalPatients.forEach((p) => {
      if (!patient.value || p.id !== patient.value.id) {
        patients.push(p);
      }
    });
  }
  return patients;
});

// Search
const searchPatient = async () => {
  searchLoading.value = true;
  searchDialogOpen.value = true;
  try {
    const response = await patientService.search(
      {
        firstName: searchCriteria.value.firstName,
        lastName: searchCriteria.value.lastName,
        phoneNumber: searchCriteria.value.phoneNumber,
        dob: searchCriteria.value.dob,
        address: searchCriteria.value.address,
        externalId: searchCriteria.value.externalId,
      },
      props.clientCompany
    );
    const results = response?.results || [];
    searchResults.value = results.map((p) => ({
      id: p.id,
      firstName: p.firstName,
      lastName: p.lastName,
      middleName: p.middleName,
      suffix: p.suffix,
      phoneNumber: p.phoneNumber,
      dob: p.dob,
      address: p.address,
      externalId: p.externalId,
      extraData: p.extraData,
      patient: p,
    }));
    // Pre-check already-selected patients and preserve current primary-first ordering.
    const searchResultsById = new Map(searchResults.value.map((p) => [p.id, p]));
    pendingPatients.value = allSelectedPatients.value
      .map((selectedPatient) => searchResultsById.get(selectedPatient.id))
      .filter(Boolean);
    pendingIds.value = pendingPatients.value.map((p) => p.id);
  } catch {
    searchResults.value = [];
  } finally {
    searchLoading.value = false;
  }
};

// Toggle patient in multi-select
const toggleSelection = (p) => {
  const index = pendingIds.value.indexOf(p.id);
  if (index >= 0) {
    pendingIds.value.splice(index, 1);
    pendingPatients.value = pendingPatients.value.filter((sp) => sp.id !== p.id);
  } else {
    pendingIds.value.push(p.id);
    pendingPatients.value.push(p);
  }
};

// Confirm selection: first = primary, rest = additional. Single emit so parent can persist in order.
const confirmSelection = () => {
  if (pendingPatients.value.length === 0) return;

  const primary = pendingPatients.value[0];
  const additional = pendingPatients.value.slice(1);
  const primaryRaw = primary.patient ?? primary;
  const additionalRaw = additional.map((p) => p.patient ?? p);

  emit("confirm-selection", primaryRaw, additionalRaw);

  searchDialogOpen.value = false;
  pendingIds.value = [];
  pendingPatients.value = [];
};

// Edit modal
const openEditModal = (p) => {
  editingPatient.value = p;
  editModalOpen.value = true;
};

const onSavePatientEdit = (patientId, changedFields) => {
  emit("update-patient", patientId, changedFields);
};

// Create modal
const openCreateModal = () => {
  const initialData = { ...searchCriteria.value };
  const requiredFields = requirementsStore.clientConfig?.intake?.requiredFields || [];

  // Handle empty values based on client requirements (same logic as PatientBar)
  Object.keys(initialData).forEach((fieldName) => {
    if (!initialData[fieldName] || initialData[fieldName] === "") {
      const isFieldRequired = requiredFields.includes(fieldName);
      if (isFieldRequired) {
        initialData[fieldName] = null;
      } else {
        delete initialData[fieldName];
      }
    }
  });

  initialData.address = null;
  initialData.externalId = null;

  newPatientDefaults.value = initialData;
  createModalOpen.value = true;
};

const onCreatePatient = (patientData) => {
  emit("save-new-patient", patientData);
};

// Remove a patient (primary or additional)
const onRemovePatient = (patientId) => {
  if (patient.value && patientId === patient.value.id) {
    // Removing the primary patient: promote the first additional patient to primary
    if (props.additionalPatients.length > 0) {
      const newPrimary = props.additionalPatients[0];
      const remainingAdditional = props.additionalPatients.slice(1);
      emit("set-patient", newPrimary);
      emit("set-additional-patients", remainingAdditional);
    } else {
      emit("set-patient", null);
    }
  } else {
    const updated = props.additionalPatients.filter((p) => p.id !== patientId);
    emit("set-additional-patients", updated);
  }
};

// Init: pre-fill search from patient guess
onMounted(() => {
  if (props.patientGuess) {
    Object.assign(searchCriteria.value, new Patient({ ...props.patientGuess }, dateAdapter));
  }
});
</script>

<style lang="scss" scoped>
.multi-patient-bar__label {
  display: flex;
  align-items: center;
  gap: 8px;
  font-family: $lh_font_family_dm_sans;
  font-size: 12.5px;
  line-height: 16px;
  font-weight: 700;
  letter-spacing: 0.07em;
  text-transform: uppercase;
  color: $lh_color_blue_gray;
  margin: 0 0 12px;
}
</style>
